import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ChevronRight, Image as ImageIcon, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { companyDetails, pagesData, galleriesData } from "../data";
import Lightbox from "../components/Lightbox";

const quickTiles = [
  {
    title: "Priser",
    description: "Se vores gennemskuelige priser på lovpakken, rutinetimer og ekstra ydelser.",
    href: "/priser",
    image: "https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/86b7dd11-d045-4b03-b2ca-81aee36dc24a.jpg",
    label: "Se priser",
  },
  {
    title: "Holdstart",
    description: "Find kommende teori- og kørekorthold, og tilmeld dig direkte online.",
    href: "/holdstart",
    image: "https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/3410097f-6c08-4059-aee2-0f17fdd0b580.jpg",
    label: "Se holdstart",
  },
  {
    title: "Om os",
    description: "Mød Køreskolen AG, læs om undervisningen og se vores nyere skolevogne.",
    href: "/om-os",
    image: "https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/a93cd808-ec0f-4c53-8d40-68823899cffd.jpg",
    label: "Læs om os",
  },
];

export default function Home() {
  const [page, setPage] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [heroReady, setHeroReady] = useState(false);

  const renderContentHtml = (html) => {
    if (!html) return "";
    const trimmed = html.trim();
    if (/<[a-z][\s\S]*>/i.test(trimmed)) return trimmed;

    return trimmed
      .split(/\n+/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)
      .map((paragraph) => `<p>${paragraph}</p>`)
      .join("");
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const siderRes = await fetch("https://api.dev.auxo.dk/public/content/sider");
        if (siderRes.ok) {
          const sider = await siderRes.json();
          const matchedPage = sider.find((entry) => {
            const termsList = entry.terms?.["side-sti"] || [];
            return termsList.includes("forside") || entry.side_sti === "forside";
          }) || sider.find((entry) => entry.page_slug === "index");

          if (matchedPage) {
            setPage({
              title: matchedPage.titel?.da || matchedPage.titel || matchedPage.title?.da || matchedPage.title || "",
              meta_description: matchedPage.meta_beskrivelse?.da || matchedPage.meta_beskrivelse || matchedPage.meta_description?.da || matchedPage.meta_description || "",
              content_html: matchedPage.indhold_html?.da || matchedPage.indhold_html || matchedPage.content_html?.da || matchedPage.content_html || "",
              images: matchedPage.billeder || matchedPage.images || [],
            });
          } else {
            setPage(pagesData.index);
          }
        } else {
          setPage(pagesData.index);
        }

        const galleriRes = await fetch("https://api.dev.auxo.dk/public/content/galleri");
        if (galleriRes.ok) {
          const galleri = await galleriRes.json();
          const matchedGallery = galleri.find((entry) => {
            const termsList = entry.terms?.["side-sti"] || [];
            return termsList.includes("forside") || entry.side_referencer?.includes("forside") || entry.slug_reference === "index";
          });

          if (matchedGallery) {
            setGallery({
              title: matchedGallery.titel?.da || matchedGallery.titel || matchedGallery.title?.da || matchedGallery.title || "",
              images: matchedGallery.billeder || matchedGallery.images || [],
            });
          } else {
            setGallery(galleriesData.index);
          }
        } else {
          setGallery(galleriesData.index);
        }
      } catch (error) {
        console.error("Fejl ved hentning af forside data:", error);
        setPage(pagesData.index);
        setGallery(galleriesData.index);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setHeroReady(true), 80);
    return () => window.clearTimeout(timer);
  }, []);

  const activePage = page || pagesData.index;
  const activeGallery = gallery || galleriesData.index;
  const heroImage =
    activePage?.images?.[0] || activeGallery?.images?.[0] || pagesData.index.images[0];

  const pageTitle = activePage?.title || "Køreskolen AG";
  const metaDescription =
    activePage?.meta_description ||
    "Leder du efter en god køreskole i Silkeborg? Bliv en del af Køreskolen AG og tag dit kørekort, hurtigt og nemt. Tilmeld dig allerede i dag";

  return (
    <div className="bg-white text-slate-800">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content="" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content="" />
      </Helmet>

      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Køreskolen AG"
            className="h-full w-full object-cover object-center opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/30 to-black/15" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-black/10" />
        </div>

        <div className="relative z-10 mx-auto flex min-h-[82vh] max-w-7xl items-end px-4 pb-10 pt-24 sm:px-6 lg:px-8 lg:pb-14 lg:pt-32">
          <div className="max-w-4xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/90 backdrop-blur-md">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
              Professionel køreundervisning i Silkeborg
            </div>

            <h1
              className={`max-w-3xl text-4xl font-black uppercase tracking-tight leading-none text-white transition-all duration-700 ease-out sm:text-5xl lg:text-6xl ${heroReady ? "translate-x-0 opacity-100" : "-translate-x-6 opacity-0"}`}
            >
              Køreskole i
              <span className="block text-emerald-400">Silkeborg</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-7 text-white/80 sm:text-lg">
              {activePage.meta_description ||
                "Drømmen om frihed på vejene begynder her hos os. Vi gør processen både spændende og lærerig."}
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/holdstart"
                className="inline-flex items-center justify-center bg-white px-6 py-3.5 text-xs font-bold uppercase tracking-[0.22em] text-slate-950 transition-colors hover:bg-slate-100"
              >
                Tilmeld hold
              </Link>
              <Link
                to="/priser"
                className="inline-flex items-center justify-center border border-white/20 bg-white/5 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.22em] text-white transition-colors hover:bg-white/10"
              >
                Se priser
              </Link>
            </div>

            <div className="mt-10 grid gap-3 text-sm text-white/80 sm:grid-cols-3">
              <div className="rounded-none border border-white/10 bg-black/20 px-4 py-3 backdrop-blur-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/55">Telefon</p>
                <a href={`tel:${companyDetails.phone.replace(/\s+/g, "")}`} className="mt-1 block font-semibold whitespace-nowrap">
                  {companyDetails.phone}
                </a>
              </div>
              <div className="rounded-none border border-white/10 bg-black/20 px-4 py-3 backdrop-blur-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/55">Email</p>
                <a href={`mailto:${companyDetails.email}`} className="mt-1 block font-semibold whitespace-nowrap">
                  {companyDetails.email}
                </a>
              </div>
              <div className="rounded-none border border-white/10 bg-black/20 px-4 py-3 backdrop-blur-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/55">Adresse</p>
                <a
                  href="https://maps.app.goo.gl/8Z1wyrsLDEbNgkmVA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 block font-semibold"
                >
                  {companyDetails.address}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro / content block */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7">
            <div className="mb-6 max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Køreskolen AG</p>
              <h2 className="mt-3 text-2xl font-black uppercase tracking-tight text-slate-950 sm:text-3xl lg:text-4xl">
                Køreskole med førstehjælp og generhvervelse af kørekort
              </h2>
              <div className="mt-5 h-px w-16 bg-emerald-500" />
            </div>

            <article
              className="max-w-none text-base leading-8 text-slate-700 [&_p]:mb-2 [&_p]:text-base [&_p]:leading-8 [&_p]:text-slate-700 [&_p]:first:mt-0 [&_h1]:mt-0 [&_h1]:mb-4 [&_h1]:text-4xl [&_h1]:font-black [&_h1]:uppercase [&_h1]:tracking-tight [&_h1]:text-slate-950 [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:text-2xl [&_h2]:font-black [&_h2]:uppercase [&_h2]:tracking-tight [&_h2]:text-slate-950 [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-slate-950 [&_h4]:mt-5 [&_h4]:mb-3 [&_h4]:text-lg [&_h4]:font-bold [&_h4]:text-slate-950 [&_ul]:my-5 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-5 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-2 [&_a]:font-semibold [&_a]:text-emerald-700 [&_a]:no-underline hover:[&_a]:underline [&_strong]:font-semibold [&_strong]:text-slate-950"
              dangerouslySetInnerHTML={{ __html: renderContentHtml(activePage.content_html) }}
            />

            <Link
              to="/kontakt"
              className="mt-8 inline-flex items-center justify-center bg-slate-950 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.22em] text-white transition-colors hover:bg-slate-900"
            >
              Kontakt os
            </Link>
          </div>

          <aside className="lg:col-span-5">
            <div className="sticky top-24 space-y-6">
              <div className="overflow-hidden bg-slate-950 text-white">
                <div className="relative h-64 sm:h-72">
                  <img
                    src="https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/81acd7f6-8885-4245-9545-bff817c604ed.jpg"
                    alt="Audi Q8 E-tron"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30" />
                  <div className="absolute inset-0 flex items-end p-6">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/70">Nyhed i vognparken</p>
                      <h3 className="mt-2 text-2xl font-black uppercase tracking-tight">Audi Q8 E-tron</h3>
                    </div>
                  </div>
                </div>
                <div className="border-t border-white/10 p-6">
                  <p className="text-sm leading-7 text-white/75">
                    Vi arbejder med nyere skolevogne og et trygt, professionelt undervisningsmiljø.
                  </p>
                </div>
              </div>

              <div className="border border-slate-200 bg-slate-50 p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Kontakt</p>
                <h3 className="mt-3 text-2xl font-black uppercase tracking-tight text-slate-950">Kontakt os i dag</h3>
                <p className="mt-4 text-sm leading-7 text-slate-600">
                  Ønsker du at tage dit kørekort hos Køreskolen AG i Silkeborg, men har spørgsmål til vores holdstart eller køreskolen?
                </p>

                <div className="mt-6 space-y-3">
                  <a
                    href={`tel:${companyDetails.phone.replace(/\s+/g, "")}`}
                    className="flex items-center gap-3 border border-slate-200 bg-white px-4 py-3 transition-colors hover:bg-slate-50"
                  >
                    <Phone className="h-4 w-4 text-emerald-600" />
                    <span className="font-semibold text-slate-900 whitespace-nowrap">{companyDetails.phone}</span>
                  </a>
                  <a
                    href={`mailto:${companyDetails.email}`}
                    className="flex items-center gap-3 border border-slate-200 bg-white px-4 py-3 transition-colors hover:bg-slate-50"
                  >
                    <Mail className="h-4 w-4 text-emerald-600" />
                    <span className="font-semibold text-slate-900 whitespace-nowrap">{companyDetails.email}</span>
                  </a>
                  <a
                    href="https://maps.app.goo.gl/8Z1wyrsLDEbNgkmVA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 border border-slate-200 bg-white px-4 py-3 transition-colors hover:bg-slate-50"
                  >
                    <MapPin className="mt-0.5 h-4 w-4 text-emerald-600" />
                    <span className="font-semibold text-slate-900">{companyDetails.address}</span>
                  </a>
                </div>

                <Link
                  to="/kontakt"
                  className="mt-6 inline-flex w-full items-center justify-center bg-slate-950 px-5 py-3.5 text-xs font-bold uppercase tracking-[0.22em] text-white transition-colors hover:bg-slate-900"
                >
                  Udfyld kontaktformular
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Featured tiles */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Hurtige genveje</p>
            <h2 className="mt-3 text-3xl font-black uppercase tracking-tight text-slate-950 sm:text-4xl">
              Find hurtigt vej til det vigtigste
            </h2>
          </div>

          <div className="grid gap-0 md:grid-cols-3">
            {quickTiles.map((tile) => (
              <Link key={tile.title} to={tile.href} className="group relative block overflow-hidden bg-slate-950">
                <img
                  src={tile.image}
                  alt={tile.title}
                  className="h-72 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/25 transition-colors group-hover:bg-black/35" />
                <div className="absolute inset-0 flex items-end p-6 sm:p-8">
                  <div className="max-w-sm text-white">
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/70">Køreskolen AG</p>
                    <h3 className="mt-2 text-2xl font-black uppercase tracking-tight">{tile.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-white/80">{tile.description}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-white">
                      {tile.label}
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Membership strip */}
      <section className="border-y border-slate-200 bg-slate-950 py-12 text-white">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-8">
          <div>
            <img
              src="https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/bf7d9bec-edad-48c7-b4b4-33a2f1085e7b.png"
              alt="Køreskolen AG Logo"
              className="mb-4 h-14 w-auto object-contain"
            />
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-400">Sikkerhed & kvalitet</p>
            <h2 className="mt-2 text-2xl font-black uppercase tracking-tight sm:text-3xl">
              Vi er medlem af Dansk Kørelærer-Union
            </h2>
          </div>
          <a
            href="https://www.dku.dk/find-koreskole/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-white px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] text-slate-950 transition-colors hover:bg-slate-100"
          >
            Læs mere her
          </a>
        </div>
      </section>

      <section className="bg-white">
        <div className="overflow-hidden rounded-none border border-slate-200 bg-slate-50 shadow-sm">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d58249.53992313496!2d9.48208639082832!3d56.16121309387021!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464b8b389224816f%3A0x92385f81aa4386a6!2sK%C3%B8reskolen%20AG!5e1!3m2!1sen!2sdk!4v1779977240199!5m2!1sen!2sdk"
            width="600"
            height="450"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="h-[450px] w-full transition duration-300 ease-out grayscale-0 brightness-100 hover:brightness-110"
          />
        </div>
      </section>
    </div>
  );
}
